import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Plus, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SubscriptionPanel from "./SubscriptionPanel";
import DiscountCodesPanel from "./DiscountCodesPanel";
import EmailSettingsPanel from "@/components/EmailSettingsPanel";
import { notifyTimeDisplayModeChanged } from "@/hooks/useTimeFormat";
import BreaksEditor, { validateBreaks, effectiveDailyHours } from "@/components/BreaksEditor";

const DAYS = [0,1,2,3,4,5,6];

export default function Settings() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const q = searchParams.get("tab");
    const allowed = ["workshop", "users", "email", "billing", "discount"];
    return allowed.includes(q) ? q : "workshop";
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin" });

  // Sync tab when URL changes (e.g. after Stripe redirect back to /settings?tab=billing)
  useEffect(() => {
    const q = searchParams.get("tab");
    const allowed = ["workshop", "users", "email", "billing", "discount"];
    if (q && allowed.includes(q) && q !== activeTab) {
      setActiveTab(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onTabChange = (v) => {
    setActiveTab(v);
    // Keep URL in sync so refresh preserves the tab without wiping Stripe params.
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/settings"), api.get("/users").catch(() => ({ data: [] }))]);
    const settings = a.data;
    // Migrate legacy `break_start` / `break_end` (single break saved before the
    // multi-break editor existed) into the `breaks` array so the UI reflects
    // the same value that /planning uses.  When the user saves we also clear
    // the legacy fields (see `save`) so they can never diverge again.
    if ((!settings.breaks || settings.breaks.length === 0) &&
        settings.break_start && settings.break_end) {
      settings.breaks = [{
        start: settings.break_start,
        end: settings.break_end,
        days: settings.working_days || [0, 1, 2, 3, 4],
      }];
    }
    setS(settings); setUsers(b.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    // Clear the legacy single-break fields whenever we persist the multi-break
    // list so the workshop capacity is computed from a single source of truth.
    await api.patch("/settings", { ...s, break_start: null, break_end: null });
    toast.success(t("common.save"));
    if (s.default_language) i18n.changeLanguage(s.default_language);
    notifyTimeDisplayModeChanged(s.time_display_mode || "aw");
  };

  const createUser = async () => {
    try {
      await api.post("/users", form);
      toast.success("OK"); setOpen(false);
      setForm({ name: "", email: "", password: "", role: "admin" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const delUser = async (id) => {
    if (!(await confirm())) return;
    await api.delete(`/users/${id}`);
    load();
  };

  if (!s) return <div className="text-zinc-400">{t("common.loading")}</div>;

  const weekdaysL = i18n.getResource(i18n.language, "translation", "weekdays") || ["Mo","Tu","We","Th","Fr","Sa","Su"];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("settings.title")}</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">{t("settings.title")}</h1>
      </div>

      <Tabs value={activeTab} onValueChange={onTabChange} className="space-y-4">
        <TabsList className="bg-[#171B24] border border-[#2E3241] flex flex-wrap gap-1 h-auto p-1">
          <TabsTrigger value="workshop" data-testid="tab-workshop">{t("settings.tab_workshop")}</TabsTrigger>
          {(user?.role === "owner" || user?.role === "admin") && (
            <TabsTrigger value="users" data-testid="tab-users">{t("settings.tab_users")}</TabsTrigger>
          )}
          {(user?.role === "owner" || user?.role === "admin") && (
            <TabsTrigger value="email" data-testid="tab-email">{t("settings.tab_email")}</TabsTrigger>
          )}
          <TabsTrigger value="billing" data-testid="tab-billing">{t("settings.tab_billing")}</TabsTrigger>
          {user?.role === "owner" && (
            <TabsTrigger value="discount" data-testid="tab-discount">{t("settings.tab_discount")}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="workshop">
          <div className="surface rounded-xl p-6 space-y-4">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("settings.workshop")}</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label className="text-xs">{t("settings.workshop_name")}</Label>
            <Input value={s.workshop_name || ""} onChange={(e) => setS({ ...s, workshop_name: e.target.value })}
              className="bg-[#171B24] border-[#2E3241]" data-testid="workshop-name" /></div>
          <div><Label className="text-xs">{t("common.phone")}</Label>
            <Input value={s.phone || ""} onChange={(e) => setS({ ...s, phone: e.target.value })} className="bg-[#171B24] border-[#2E3241]" /></div>
          <div><Label className="text-xs">{t("common.email")}</Label>
            <Input value={s.email || ""} onChange={(e) => setS({ ...s, email: e.target.value })} className="bg-[#171B24] border-[#2E3241]" /></div>
          <div><Label className="text-xs">{t("common.address")}</Label>
            <Input value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} className="bg-[#171B24] border-[#2E3241]" /></div>
          <div>
            <Label className="text-xs">{t("settings.default_language")}</Label>
            <Select value={s.default_language} onValueChange={(v) => setS({ ...s, default_language: v })}>
              <SelectTrigger className="bg-[#171B24] border-[#2E3241]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                <SelectItem value="de">Deutsch</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="ro">Română</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border-t border-[#2E3241] pt-4 mt-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">{t("settings.hourly_rate")}</div>
          <div className="flex items-center gap-2 max-w-xs">
            <Input
              type="number" step="0.01" min="0"
              value={s.hourly_rate ?? 100}
              onChange={(e) => setS({ ...s, hourly_rate: parseFloat(e.target.value) || 0 })}
              className="bg-[#171B24] border-[#2E3241]"
              data-testid="settings-hourly-rate"
            />
            <span className="text-sm text-zinc-400 whitespace-nowrap">€ / {t("common.hour_short")}</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">{t("settings.hourly_rate_hint")}</div>
        </div>

        <div className="border-t border-[#2E3241] pt-4 mt-4 space-y-3" data-testid="settings-pricing-block">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("settings.prices_vat")}</div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" className="h-4 w-4 accent-blue-500"
              checked={s.use_prices !== false}
              onChange={(e) => setS({ ...s, use_prices: e.target.checked })}
              data-testid="settings-use-prices" />
            <span className="text-sm text-zinc-200">{t("settings.use_prices")}</span>
          </label>
          <div className="text-[11px] text-zinc-500">{t("settings.use_prices_hint")}</div>
          {s.use_prices !== false && (
            <div className="flex items-center gap-2 max-w-xs">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 min-w-[140px]">
                {t("settings.default_vat_rate")}
              </div>
              <Input type="number" step="0.1" min="0" max="100"
                value={s.default_vat_rate ?? 19}
                onChange={(e) => setS({ ...s, default_vat_rate: parseFloat(e.target.value) || 0 })}
                className="bg-[#171B24] border-[#2E3241]"
                data-testid="settings-default-vat" />
              <span className="text-sm text-zinc-400">%</span>
            </div>
          )}
        </div>

        <div className="border-t border-[#2E3241] pt-4 mt-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">{t("settings.time_display")}</div>
          <Select value={s.time_display_mode || "aw"} onValueChange={(v) => setS({ ...s, time_display_mode: v })}>
            <SelectTrigger className="bg-[#171B24] border-[#2E3241] w-64" data-testid="time-display-mode-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
              <SelectItem value="aw">{t("settings.time_display_aw")}</SelectItem>
              <SelectItem value="hours">{t("settings.time_display_hours")}</SelectItem>
              <SelectItem value="both">{t("settings.time_display_both")}</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-[11px] text-zinc-500 mt-1">{t("settings.time_display_hint")}</div>
        </div>

        <div className="border-t border-[#2E3241] pt-4 mt-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">{t("settings.working_hours")}</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs">{t("employees.start")}</Label>
              <Input type="time" value={s.start_time || "08:00"} onChange={(e) => setS({ ...s, start_time: e.target.value })}
                className="bg-[#171B24] border-[#2E3241]" data-testid="workshop-start-time" /></div>
            <div><Label className="text-xs">{t("employees.end")}</Label>
              <Input type="time" value={s.end_time || "17:00"} onChange={(e) => setS({ ...s, end_time: e.target.value })}
                className="bg-[#171B24] border-[#2E3241]" data-testid="workshop-end-time" /></div>
          </div>
          <div className="mt-3">
            <Label className="text-xs">{t("employees.working_days")}</Label>
            <div className="flex gap-1 mt-1">
              {DAYS.map((d) => (
                <button key={d} type="button"
                  onClick={() => setS({
                    ...s,
                    working_days: (s.working_days || []).includes(d)
                      ? s.working_days.filter((x) => x !== d)
                      : [...(s.working_days || []), d].sort(),
                  })}
                  className={`h-8 w-10 rounded text-xs font-medium border ${
                    (s.working_days || []).includes(d)
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-[#171B24] text-zinc-400 border-[#2E3241]"
                  }`}>{weekdaysL[d]}</button>
              ))}
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <div className="text-xs uppercase tracking-widest text-zinc-500">{t("settings.breaks")}</div>
            <BreaksEditor
              value={s.breaks || []}
              onChange={(v) => setS({ ...s, breaks: v })}
              workingDays={s.working_days || [0,1,2,3,4]}
              startTime={s.start_time || "08:00"}
              endTime={s.end_time || "17:00"}
              hint={t("settings.breaks_hint")}
            />
            <div className="text-xs text-zinc-400 mt-2" data-testid="workshop-effective-capacity">
              {t("settings.available_work_time")}:{" "}
              <span className="font-mono-stat text-emerald-300">
                {effectiveDailyHours({
                  start_time: s.start_time || "08:00",
                  end_time: s.end_time || "17:00",
                  breaks: s.breaks || [],
                })} h
              </span>
            </div>
          </div>
        </div>

        <Button onClick={save}
          disabled={validateBreaks(s.breaks || [], s.start_time || "08:00", s.end_time || "17:00", t).errors.length > 0}
          className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50" data-testid="settings-save">{t("common.save")}</Button>
          </div>
        </TabsContent>

        {(user?.role === "owner" || user?.role === "admin") && (
          <TabsContent value="users">
            <div className="surface rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("settings.users")}</div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-500 hover:bg-blue-600" data-testid="new-user-btn"><Plus className="h-4 w-4 mr-1" /> {t("common.new")}</Button>
              </DialogTrigger>
              <DialogContent className="bg-[#21252E] border-[#2E3241] text-white">
                <DialogHeader><DialogTitle>{t("common.new")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs">{t("common.name")}</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#171B24] border-[#2E3241]" /></div>
                  <div><Label className="text-xs">{t("common.email")}</Label>
                    <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#171B24] border-[#2E3241]" /></div>
                  <div><Label className="text-xs">{t("auth.password")}</Label>
                    <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#171B24] border-[#2E3241]" /></div>
                  <div>
                    <Label className="text-xs">{t("employees.role")}</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger className="bg-[#171B24] border-[#2E3241]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                        {user?.role === "owner" && <SelectItem value="owner">Owner</SelectItem>}
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="mechanic">Mechanic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                  <Button onClick={createUser} className="bg-blue-500 hover:bg-blue-600" data-testid="create-user-submit">{t("common.create")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <table className="w-full text-sm">
            <thead className="text-zinc-400">
              <tr>
                <th className="text-left py-2 text-xs uppercase tracking-widest">{t("common.email")}</th>
                <th className="text-left py-2 text-xs uppercase tracking-widest">{t("common.name")}</th>
                <th className="text-left py-2 text-xs uppercase tracking-widest">{t("employees.role")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-[#2E3241]">
                  <td className="py-2">{u.email}</td>
                  <td className="py-2 text-zinc-400">{u.name}</td>
                  <td className="py-2"><span className="text-xs uppercase tracking-wider text-zinc-400">{u.role}</span></td>
                  <td className="py-2 text-right">
                    {user?.role === "owner" && u.id !== user.id && (
                      <Button size="icon" variant="ghost" onClick={() => delUser(u.id)} className="text-zinc-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </TabsContent>
        )}

        <TabsContent value="billing">
          <SubscriptionPanel />
        </TabsContent>

        {(user?.role === "owner" || user?.role === "admin") && (
          <TabsContent value="email">
            <EmailSettingsPanel s={s} setS={setS} onSave={save} />
          </TabsContent>
        )}

        {user?.role === "owner" && (
          <TabsContent value="discount">
            <DiscountCodesPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
